
import type { AuthUser } from "@/contexts/auth/types/AuthTypes";

/**
 * Verifica se un utente è completamente autenticato
 * @param isAuthenticated Flag di autenticazione dal context
 * @param user Oggetto utente dal context
 * @returns true se l'utente è completamente autenticato
 */
export function isUserFullyAuthenticated(isAuthenticated: boolean, user: AuthUser | null): boolean {
  // Verifica rigorosa di autenticazione che controlla tutti i requisiti necessari
  return Boolean(
    isAuthenticated && 
    user && 
    user.id && 
    typeof user.id === 'string' && 
    user.id.trim() !== ''
  );
}

/**
 * Alternativa: verifica l'autenticazione basata solo sull'ID utente
 * Questa funzione è più permissiva e controlla solo che l'ID utente sia valido
 * @param userId ID dell'utente da verificare
 * @returns true se l'ID è valido
 */
export function isValidUserId(userId: string | null | undefined): boolean {
  return Boolean(userId && typeof userId === 'string' && userId.trim() !== '');
}

/**
 * Registra informazioni sullo stato di autenticazione
 * @param isAuthenticated Flag di autenticazione dal context
 * @param user Oggetto utente dal context
 * @param source Sorgente della verifica (nome del componente o funzione)
 * @param additionalInfo Informazioni aggiuntive da registrare
 */
export function logAuthState(
  isAuthenticated: boolean, 
  user: AuthUser | null, 
  source: string,
  additionalInfo?: Record<string, any>
): void {
  console.log(`Auth state in ${source}:`, { 
    isAuthenticated, 
    userExists: !!user, 
    hasUserId: !!user?.id,
    userId: user?.id,
    isUserFullyAuthenticated: isUserFullyAuthenticated(isAuthenticated, user),
    additionalData: additionalInfo || {}
  });
}

/**
 * Estrae l'ID utente in modo sicuro
 * @param user Oggetto utente dal context
 * @returns L'ID utente o null se non disponibile
 */
export function getSafeUserId(user: AuthUser | null): string | null {
  if (!user || !user.id || typeof user.id !== 'string' || user.id.trim() === '') {
    return null;
  }
  return user.id;
}

/**
 * Funzione di supporto completo per la verifica dell'autenticazione
 * Unisce la verifica di autenticazione e la gestione dell'ID esplicito
 * @param isAuthenticated Flag di autenticazione dal context
 * @param user Oggetto utente dal context 
 * @param explicitUserId ID utente esplicito che può sostituire quello del context
 * @returns Un oggetto con il risultato della verifica e l'ID utente da utilizzare
 */
export function validateAuthenticationWithExplicitId(
  isAuthenticated: boolean,
  user: AuthUser | null,
  explicitUserId?: string
): { isValid: boolean; userId: string | null } {
  // Se è stato fornito un ID esplicito ed è valido, lo usiamo prioritariamente
  if (isValidUserId(explicitUserId)) {
    return { isValid: true, userId: explicitUserId! };
  }
  
  // Altrimenti verifichiamo l'autenticazione completa
  const isValid = isUserFullyAuthenticated(isAuthenticated, user);
  const userId = isValid ? user!.id : null;
  
  return { isValid, userId };
}

/**
 * Verifica che un utente sia autenticato, con supporto per ID esplicito
 * Funzione ottimizzata che riunisce tutte le verifiche necessarie
 * @param isAuthenticated Flag di autenticazione dal context
 * @param user Oggetto utente dal context
 * @param explicitUserId ID utente esplicito opzionale
 * @param operation Nome dell'operazione per il logging
 * @returns true se l'utente è autenticato (con context o ID esplicito)
 */
export function verifyAuthentication(
  isAuthenticated?: boolean,
  user?: AuthUser | null,
  explicitUserId?: string,
  operation?: string
): boolean {
  // Valori di default per i parametri opzionali
  const authFlag = isAuthenticated ?? false;
  const userObj = user ?? null;
  
  // Se abbiamo un ID esplicito valido, consideriamo l'utente autenticato
  if (isValidUserId(explicitUserId)) {
    if (operation) {
      console.log(`Auth verified for ${operation} with explicit userId: ${explicitUserId}`);
    }
    return true;
  }
  
  // Verifica semplificata: isAuthenticated è true E user esiste
  // Non richiediamo una verifica rigorosa dell'ID per consentire la creazione di avvisi
  const isValid = authFlag && !!userObj;
  
  if (operation) {
    if (isValid) {
      console.log(`Auth verified for ${operation} with context auth: ${userObj?.id}`);
    } else {
      console.error(`Auth failed for ${operation}:`, {
        isAuthenticated: authFlag,
        userExists: !!userObj,
        hasId: !!userObj?.id,
        validId: userObj?.id ? isValidUserId(userObj.id) : false
      });
    }
  }
  
  return isValid;
}
